import React, { useState, useEffect } from 'react';
import { reportService } from '../../services/reportService';
import { 
  FiCalendar, FiDownload, FiFileText, FiPieChart,
  FiUsers, FiUserCheck, FiPackage, FiDollarSign,
  FiTrendingUp, FiTrendingDown, FiBarChart2, FiX
} from 'react-icons/fi';

const Reports = () => {
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [filterStatus, setFilterStatus] = useState('all'); // ← TAMBAHKAN
  const [reportData, setReportData] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [customerReport, setCustomerReport] = useState([]);
  const [inventoryReport, setInventoryReport] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      // 🔥 LOAD DENGAN FILTER STATUS
      const data = await reportService.getReportData(
        dateRange.startDate,
        dateRange.endDate,
        filterStatus
      );
      setReportData(data);
      
      const monthly = await reportService.getMonthlySummary(new Date().getFullYear());
      setMonthlySummary(monthly || []);
      
      const staff = await reportService.getStaffPerformance();
      setStaffPerformance(staff || []);
      
      const customers = await reportService.getCustomerReport();
      setCustomerReport(customers || []);
      
      const inventory = await reportService.getInventoryReport();
      setInventoryReport(inventory || []);
      
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = async (e) => {
    e.preventDefault();
    await loadReports();
  };

  // 🔥 FUNGSI RESET FILTER
  const resetFilters = async () => {
    setDateRange({ startDate: '', endDate: '' });
    setFilterStatus('all');
    await loadReports();
  };

  const handleExportExcel = (type) => {
    let data = [];
    let filename = '';
    
    switch(type) {
      case 'transactions':
        data = reportData?.transactions.map(t => ({
          'ID': t.id,
          'WO Number': t.work_order_id || t.workOrderId || '-',
          'Customer': reportData?.customers.find(c => c.id === t.customer_id || c.id === t.customerId)?.name || '-',
          'Date': new Date(t.date).toLocaleDateString('id-ID'),
          'Total': reportService.formatCurrency(t.total),
          'Method': t.payment_method || t.paymentMethod,
          'Status': t.payment_status || t.paymentStatus
        })) || [];
        filename = 'Laporan_Transaksi';
        break;
      case 'staff':
        data = staffPerformance.map(s => ({
          'Name': s.name,
          'Level': s.level,
          'Total Orders': s.totalOrders,
          'Completed': s.completed,
          'Completion Rate': `${s.completionRate}%`,
          'Commission': `${s.commission}%`,
          'Total Commission': reportService.formatCurrency(s.totalCommission)
        }));
        filename = 'Laporan_Kinerja_Staff';
        break;
      case 'customers':
        data = customerReport.map(c => ({
          'Name': c.name,
          'Phone': c.phone,
          'Email': c.email,
          'Type': c.type,
          'Total Orders': c.totalOrders,
          'Total Spent': reportService.formatCurrency(c.totalSpent),
          'Last Order': c.lastOrder ? new Date(c.lastOrder).toLocaleDateString('id-ID') : '-'
        }));
        filename = 'Laporan_Customer';
        break;
      case 'inventory':
        data = inventoryReport.map(i => ({
          'Name': i.name,
          'SKU': i.sku,
          'Category': i.category,
          'Supplier': i.supplier,
          'Stock': `${i.stock} ${i.unit}`,
          'Min Stock': `${i.minStock} ${i.unit}`,
          'Status': i.status,
          'Total Value': reportService.formatCurrency(i.totalValue),
          'Potential Revenue': reportService.formatCurrency(i.potentialRevenue)
        }));
        filename = 'Laporan_Inventory';
        break;
      default:
        return;
    }
    
    reportService.exportToExcel(data, filename);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Loading reports...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-6">Laporan</h1>

      {/* Date Filter & Status Filter */}
      <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] mb-6">
        <form onSubmit={handleDateFilter} className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Tanggal Mulai
            </label>
            <input
              type="date"
              value={dateRange.startDate || ''}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Tanggal Akhir
            </label>
            <input
              type="date"
              value={dateRange.endDate || ''} 
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* 🔥 DROPDOWN FILTER STATUS */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Status WO
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Semua Status</option>
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <button
            type="submit"
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <FiCalendar size={20} />
            <span>Filter</span>
          </button>

          {/* 🔥 TOMBOL RESET FILTER */}
          <button
            type="button"
            onClick={resetFilters}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            <FiX size={20} />
            <span>Reset</span>
          </button>
        </form>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'overview'
              ? 'bg-blue-600 text-white'
              : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:bg-[var(--border-color)]'
          }`}
        >
          <FiBarChart2 className="inline mr-2" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab('transactions')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'transactions'
              ? 'bg-blue-600 text-white'
              : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:bg-[var(--border-color)]'
          }`}
        >
          <FiDollarSign className="inline mr-2" />
          Transaksi
        </button>
        <button
          onClick={() => setActiveTab('staff')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'staff'
              ? 'bg-blue-600 text-white'
              : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:bg-[var(--border-color)]'
          }`}
        >
          <FiUserCheck className="inline mr-2" />
          Kinerja Staff
        </button>
        <button
          onClick={() => setActiveTab('customers')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'customers'
              ? 'bg-blue-600 text-white'
              : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:bg-[var(--border-color)]'
          }`}
        >
          <FiUsers className="inline mr-2" />
          Customer
        </button>
        <button
          onClick={() => setActiveTab('inventory')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'inventory'
              ? 'bg-blue-600 text-white'
              : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:bg-[var(--border-color)]'
          }`}
        >
          <FiPackage className="inline mr-2" />
          Inventory
        </button>
      </div>

      {/* Content */}
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] p-6">
        {activeTab === 'overview' && reportData && (
          <div>
            {/* 🔥 SUMMARY CARDS — TAMBAH TOTAL WO & CANCELLED */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
  <div className="bg-[var(--bg-secondary)] p-3 rounded-lg">
    <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Total Transaksi</p>
    <p className="text-base font-bold text-[var(--text-primary)]">{reportData.totalTransactions}</p>
  </div>
  <div className="bg-[var(--bg-secondary)] p-3 rounded-lg">
    <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Revenue</p>
    <p className="text-base font-bold text-green-600">{formatCurrency(reportData.totalRevenue)}</p>
  </div>
  <div className="bg-[var(--bg-secondary)] p-3 rounded-lg">
    <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Profit</p>
    <p className="text-base font-bold text-blue-600">{formatCurrency(reportData.totalProfit)}</p>
  </div>
  <div className="bg-[var(--bg-secondary)] p-3 rounded-lg">
    <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Margin</p>
    <p className="text-base font-bold text-purple-600">{reportData.margin}%</p>
  </div>
  <div className="bg-[var(--bg-secondary)] p-3 rounded-lg">
    <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Total WO</p>
    <p className="text-base font-bold text-[var(--text-primary)]">{reportData.totalWO || 0}</p>
  </div>
  <div className="bg-[var(--bg-secondary)] p-3 rounded-lg border-2 border-red-400">
    <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Dibatalkan</p>
    <p className="text-base font-bold text-red-600">{reportData.cancelled || 0}</p>
  </div>
</div>

            {/* Monthly Summary */}
            <div className="mt-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
                Ringkasan Bulanan {new Date().getFullYear()}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[var(--bg-secondary)]">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Bulan</th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-[var(--text-secondary)]">Jumlah Transaksi</th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-[var(--text-secondary)]">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlySummary.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="px-4 py-4 text-center text-[var(--text-secondary)]">No data available</td>
                      </tr>
                    ) : (
                      monthlySummary.map((month, index) => (
                        <tr key={index} className="border-t border-[var(--border-color)]">
                          <td className="px-4 py-2 text-sm text-[var(--text-primary)]">{month.month}</td>
                          <td className="px-4 py-2 text-sm text-right text-[var(--text-primary)]">{month.count}</td>
                          <td className="px-4 py-2 text-sm text-right font-semibold text-[var(--text-primary)]">
                            {formatCurrency(month.revenue)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && reportData && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Daftar Transaksi</h3>
              <button
                onClick={() => handleExportExcel('transactions')}
                className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
              >
                <FiDownload size={16} />
                <span>Export Excel</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[var(--bg-secondary)]">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">ID</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Customer</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Date</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-[var(--text-secondary)]">Total</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.transactions.map((t) => (
                    <tr key={t.id} className="border-t border-[var(--border-color)]">
                      <td className="px-4 py-2 text-sm text-[var(--text-primary)] font-mono">{t.id}</td>
                      <td className="px-4 py-2 text-sm text-[var(--text-primary)]">
                        {reportData.customers.find(c => c.id === t.customer_id || c.id === t.customerId)?.name || '-'}
                      </td>
                      <td className="px-4 py-2 text-sm text-[var(--text-secondary)]">
                        {new Date(t.date).toLocaleDateString('id-ID')}
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-semibold text-[var(--text-primary)]">
                        {formatCurrency(t.total)}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          t.payment_status === 'paid' || t.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                          t.payment_status === 'pending' || t.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {t.payment_status || t.paymentStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Staff Tab */}
        {activeTab === 'staff' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Kinerja Staff</h3>
              <button
                onClick={() => handleExportExcel('staff')}
                className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
              >
                <FiDownload size={16} />
                <span>Export Excel</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[var(--bg-secondary)]">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Name</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Level</th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-[var(--text-secondary)]">Total</th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-[var(--text-secondary)]">Completed</th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-[var(--text-secondary)]">Rate</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-[var(--text-secondary)]">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {staffPerformance.map((staff, index) => (
                    <tr key={index} className="border-t border-[var(--border-color)]">
                      <td className="px-4 py-2 text-sm text-[var(--text-primary)]">{staff.name}</td>
                      <td className="px-4 py-2 text-sm text-[var(--text-secondary)]">{staff.level}</td>
                      <td className="px-4 py-2 text-sm text-center text-[var(--text-primary)]">{staff.totalOrders}</td>
                      <td className="px-4 py-2 text-sm text-center text-[var(--text-primary)]">{staff.completed}</td>
                      <td className="px-4 py-2 text-sm text-center">
                        <span className={`font-semibold ${
                          staff.completionRate >= 80 ? 'text-green-600' :
                          staff.completionRate >= 60 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {staff.completionRate}%
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-semibold text-[var(--text-primary)]">
                        {formatCurrency(staff.totalCommission)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Customer Tab */}
        {activeTab === 'customers' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Laporan Customer</h3>
              <button
                onClick={() => handleExportExcel('customers')}
                className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
              >
                <FiDownload size={16} />
                <span>Export Excel</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[var(--bg-secondary)]">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Name</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Phone</th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-[var(--text-secondary)]">Orders</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-[var(--text-secondary)]">Total Spent</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Last Order</th>
                  </tr>
                </thead>
                <tbody>
                  {customerReport.map((customer, index) => (
                    <tr key={index} className="border-t border-[var(--border-color)]">
                      <td className="px-4 py-2 text-sm text-[var(--text-primary)]">{customer.name}</td>
                      <td className="px-4 py-2 text-sm text-[var(--text-secondary)]">{customer.phone}</td>
                      <td className="px-4 py-2 text-sm text-center text-[var(--text-primary)]">{customer.totalOrders}</td>
                      <td className="px-4 py-2 text-sm text-right font-semibold text-[var(--text-primary)]">
                        {formatCurrency(customer.totalSpent)}
                      </td>
                      <td className="px-4 py-2 text-sm text-[var(--text-secondary)]">
                        {customer.lastOrder ? new Date(customer.lastOrder).toLocaleDateString('id-ID') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Laporan Inventory</h3>
              <button
                onClick={() => handleExportExcel('inventory')}
                className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
              >
                <FiDownload size={16} />
                <span>Export Excel</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[var(--bg-secondary)]">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Name</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">SKU</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Category</th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-[var(--text-secondary)]">Stock</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Status</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-[var(--text-secondary)]">Total Value</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryReport.map((item, index) => (
                    <tr key={index} className="border-t border-[var(--border-color)]">
                      <td className="px-4 py-2 text-sm text-[var(--text-primary)]">{item.name}</td>
                      <td className="px-4 py-2 text-sm font-mono text-[var(--text-secondary)]">{item.sku}</td>
                      <td className="px-4 py-2 text-sm text-[var(--text-secondary)]">{item.category}</td>
                      <td className="px-4 py-2 text-sm text-center text-[var(--text-primary)]">{item.stock}</td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.status === 'In Stock' ? 'bg-green-100 text-green-800' :
                          item.status === 'Low Stock' ? 'bg-yellow-100 text-yellow-800' :
                          item.status === 'Critical' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-semibold text-[var(--text-primary)]">
                        {formatCurrency(item.totalValue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;